import { queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * LotosKpi desglosa los LOTOs por estado. El ciclo de vida es binario (#241): `abierto` son
 * los LOTOs vivos (los que exigen atención en planta) y `cerrados`, los finalizados.
 */
export type LotosKpi = {
  abierto: number;
  cerrados: number;
};

/** Kpis son los indicadores del encabezado del panel (GET /api/v1/dashboard/kpis). */
export type Kpis = {
  lotos: LotosKpi;
  equipos_bloqueados: number;
  personal_habilitado: number;
};

/**
 * CandadoActivo es un candado colgado en planta (GET /api/v1/dashboard/candados-activos):
 * dónde está (área/equipo/puntos), qué candado, quién lo colocó y cuándo. `puntos` son los
 * códigos de los puntos AÚN cubiertos por ese candado separados por coma (#244: un candado
 * puede cubrir varios; el panel sigue mostrando una fila por candado). `operario` puede ser
 * "" si quien lo colocó ya no existe.
 */
export type CandadoActivo = {
  id_bloqueo: number;
  id_loto: number;
  numero_loto: string;
  area: string;
  equipo: string;
  codigo_equipo: string;
  puntos: string;
  codigo_candado: string;
  operario: string;
  fecha_colocacion: string;
};

/**
 * EstadoEquipo es el estado operativo calculado de un equipo (no una columna): lo deriva el
 * backend de si alguno de sus puntos sigue cubierto por un candado puesto. Es BINARIO
 * (#243) y coincide con el de `lib/equipos.ts`: misma definición en todo el sistema.
 * Muy visual: BLOQUEADO rojo / DESBLOQUEADO verde.
 */
export type EstadoEquipo = "BLOQUEADO" | "DESBLOQUEADO";

/**
 * LotoReciente es un LOTO del panel de recientes (GET /api/v1/dashboard/lotos-recientes)
 * con el ESTADO REAL de su equipo, calculado ahora (no el estado del LOTO).
 */
export type LotoReciente = {
  id: number;
  numero_loto: string;
  estado: string;
  area: string;
  equipo: string;
  codigo_equipo: string;
  estado_equipo: EstadoEquipo;
  fecha_apertura: string;
};

/**
 * dashboardQueryKey namespacea la caché del panel POR obra: `["obra", obraId, "dashboard"]`
 * (F7 #158). Las tres lecturas del panel (kpis, candados activos, lotos recientes) cuelgan
 * de esta raíz, de modo que el panel de una obra jamás casa con el de otra.
 */
export function dashboardQueryKey(obraId: number) {
  return ["obra", obraId, "dashboard"] as const;
}

/**
 * kpisQueryOptions lee los indicadores del panel de la obra `obraId`. Se refrescan al
 * reentrar (el panel es un aterrizaje operativo en tiempo real), sin caché larga.
 */
export function kpisQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: [...dashboardQueryKey(obraId), "kpis"],
    queryFn: () => api.get<Kpis>(`/obras/${obraId}/dashboard/kpis`),
  });
}

export function candadosActivosQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: [...dashboardQueryKey(obraId), "candados-activos"],
    queryFn: () => api.get<CandadoActivo[]>(`/obras/${obraId}/dashboard/candados-activos`),
  });
}

export function lotosRecientesQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: [...dashboardQueryKey(obraId), "lotos-recientes"],
    queryFn: () => api.get<LotoReciente[]>(`/obras/${obraId}/dashboard/lotos-recientes`),
  });
}

export function useKpis(obraId: number) {
  return useQuery(kpisQueryOptions(obraId));
}

export function useCandadosActivos(obraId: number) {
  return useQuery(candadosActivosQueryOptions(obraId));
}

export function useLotosRecientes(obraId: number) {
  return useQuery(lotosRecientesQueryOptions(obraId));
}
